<template>
  <div class="cards-wrapper">
    <div class="spinner-container" v-if="isLoading">
      <base-spinner
        :is-loading="true"
        :is-local="true"
        class="spinner"
      />
    </div>
    <div class="cards-container" v-if="!isLoading">
      <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
      <div class="empty-container" v-if="isCardsListEmpty">{{ $t('message.cardsPage.empty') }}</div>
      <div class="cards-block" v-if="!isCardsListEmpty">
        <card
          v-for="item of cardsList"
          :key="item.id"
          :data="item"
          :defaultCardId="profile.default_card_id"
          @deleteCard="openDeleteCardModal"
        />
      </div>
      <div class="amount-notice">
        {{ $t('message.cardsPage.amountNotice') }}
      </div>
      <div class="btn-container">
        <base-button
          :text="$t(`message.cardsPage.${this.isCardsListEmpty ? 'addNewBtn' : 'addEnotherBtn'}`)"
          @click="addNewCard()"
          :is-loading="isAddBtnLoading"
        />
      </div>
    </div>
    <base-modal
      v-if="isShowDeleteCardModal"
      @close="closeDeleteCardModal()"
    >
      <template #header>
        <div class="modal-header">
          <img @click.stop="closeDeleteCardModal()" class="close-image" src="../../../public/custom/images/cross.svg" alt="Cross image">
        </div>
        <div class="notice-title">
          {{ $t('message.cardsPage.deleteCard.title') }}
        </div>
        <div class="notice-text">
          {{ $t('message.cardsPage.deleteCard.text') }}
        </div>
        <div class="btns-block">
          <div class="accept-btn-block">
            <base-button
              :text="$t('message.cardsPage.deleteCard.acceptBtn')"
              class="accept-btn"
              @click="deleteCard()"
            />
          </div>
          <div class="cancel-btn-block">
            <base-button
              :text="$t('message.cardsPage.deleteCard.closeBtn')"
              class="cancel-btn"
              @click="closeDeleteCardModal()"
            />
          </div>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script>
import BaseButton from '../../components/base/BaseButton.vue';
import BaseSpinner from '../../components/base/BaseSpinner.vue';
import Card from './Card.vue';
import { mapGetters } from 'vuex';
import BaseModal from '../../components/base/BaseModal.vue';

export default {
  name: 'Cards',
  inject: ['$httpService'],
  components: {
    BaseButton,
    BaseSpinner,
    Card,
    BaseModal
  },
  async created() {
    try {
      await this.$store.dispatch('getCardsList');

      if (this.cardsList.length === 1) {
        if (this.cardsList[0].id !== this.profile.default_card_id) {
          await this.$store.dispatch('setDefaultCard', this.cardsList[0].id);
        }
      }
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  },
  data() {
    return {
      isLoading: true,
      isAddBtnLoading: false,
      isShowDeleteCardModal: false,
      deleteCardId: ''
    }
  },
  methods: {
    async addNewCard() {
      this.isLoading = true;
      const returnUrl = window.location.href;
      const lang = this.$i18n.locale;
      const type = 'card_page';
      const stateInProgress = 'no';

      const payload = {returnUrl, lang, type, stateInProgress};

      try {
        const response = await this.$httpService.card.addNewCard(payload);
        window.location.href = response.data.url;
      } catch (error) {
        this.isLoading = false;
      }
    },
    openDeleteCardModal(card_id) {
      this.deleteCardId = card_id;
      this.isShowDeleteCardModal = true;
    },
    closeDeleteCardModal() {
      this.deleteCardId = '';
      this.isShowDeleteCardModal = false;
    },
    async deleteCard() {
      this.isShowDeleteCardModal = false;
      this.isLoading = true;
      try {
        await this.$httpService.card.deleteCard(this.deleteCardId);
        await this.$store.dispatch('getCardsList');
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    }
  },
  computed: {
    ...mapGetters(['cardsList', 'profile']),
    isCardsListEmpty() {
      return this.cardsList.length === 0;
    }
  }
}
</script>

<style lang="scss" scoped>
  .cards-wrapper {
    @include page-wrapper-with-header-and-footer;

    padding-top: 60px;
    box-sizing: border-box;
    background-color: var(--profile-page-bgc);
  }

  .cards-container,
  .spinner-container {
    @include base-page-container;

    display: flex;
    flex-direction: column;
    align-items: center;
    color: var(--profile-text-color);
  }

  .spinner-container {
    justify-content: center;
    margin-top: 10%;
  }

  .spinner ::v-deep {
    width: 100%;
  }

  .empty-container {
    font-weight: 500;
    font-size: 32px;
    line-height: 39px;
    letter-spacing: 0.01em;
    color: #121212;
    text-align: center;
  }

  .cards-block {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .btn-container {
    width: 300px;
    height: 65px;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .modal-header {
    @include modal-close-block;
  }

  .notice-title {
    margin-bottom: 10px;
    font-weight: 700;
    font-size: 22px;
    line-height: 125%;
    text-align: center;
    color: #EB5757;
  }

  .notice-text {
    margin-bottom: 30px;
    font-weight: 500;
    font-size: 20px;
    line-height: 125%;
    text-align: center;
    color: #101010;
  }

  .btns-block {
    display: flex;
    margin-bottom: 30px;
  }

  .accept-btn-block {
    width: 140px;
    height: 30px;
    margin-right: 20px;
  }

  .cancel-btn-block {
    width: 130px;
    height: 30px;
  }

  .accept-btn ::v-deep {
    border: 2px solid #41BF7B;
  }

  .cancel-btn ::v-deep {
    background-color: white;
    border: 2px solid #41BF7B;
    box-sizing: border-box;
    color: #41BF7B;
  }

  .route-name {
    @include route-name;
    width: 95%;
    text-align: start;
    margin-bottom: 40px;
    margin-top: -20px;
  }

  .amount-notice {
    margin-top: 65px;
    padding: 30px;
    box-sizing: border-box;
    width: fit-content;
    background: #F8F8F8;
    border: 1px solid #F49332;
    border-radius: 20px;
    font-weight: 500;
    font-size: 18px;
    line-height: 22px;
    letter-spacing: 0.01em;
    color: #EB5757;
    text-align: center;
  }
</style>